// Ultimate Card Game (UCG) - Voidglass Battlefield Background
//
// Fourth battlefield backdrop (js/engine/maps.js selects between 'human',
// 'sul', 'swamp', and 'voidglass'). Ported from the standalone art prototype
// at sandbox/voidglass-map/ into the real renderer's contract:
// renderVoidglassStaticBackground/renderVoidglassStaticForeground are baked
// once into the offscreen world-space canvases by
// Renderer.ensureStaticBackground() (see renderer.js), while
// renderVoidglassDynamicBackground/renderVoidglassDynamicForeground/
// renderVoidglassForegroundCritters run every frame on top of that cached
// bake - same split sulBackgroundMixin/swampBackgroundMixin use. A ninth
// method, renderVoidglassFrame, is NOT part of that contract - it is a
// screen-space post-process atmosphere overlay (same category as
// postProcessEffects.js's renderObservationGlassCanopy), called directly
// from renderPostProcess() instead of being baked or camera-transformed.
//
// The concept: the lane is a transparent armored glass deck bolted to a
// ship hurtling through deep space. Units stand on real physical glass
// (panels/rivets/scratches/frost/refraction); the starfield streaming past
// underneath is seen THROUGH that glass, optically bent.
//
// Two structural departures from the sandbox that matter for correctness:
//
// 1. Seed-cached layout, not module-level mutable state. The sandbox keeps
//    stars/planets/scratches/frost/nebula as bare `let` arrays rebuilt by a
//    UI-bound regenerate(). Here buildLayout(seed, width, height, laneY,
//    laneH) is a pure function of a server-/session-issued battlefield.mapSeed,
//    cached on the renderer instance by getLayout() - identical pattern to
//    backgroundSul.js's buildLayout/getLayout. Rare-event cycle offsets
//    (comet/satellite/foreground-comet) are the one exception: like
//    backgroundSul.js's own LIGHTNING_CYCLE offset, they use plain
//    Math.random() cached per-renderer-instance, since cosmetic timing never
//    needs cross-peer determinism, only gameplay does.
//
// 2. The glass "refraction" (sandbox's applyGlassRefraction) reads back
//    pixels it just drew to fake the starfield bending under the glass. On
//    the sandbox's single always-static <canvas> that self-read is safe. In
//    the real renderer, the live ctx has a camera transform applied
//    (viewport pan/zoom/DPR, screen-shake, punch-zoom) BEFORE
//    renderBackgroundCached() runs - reading back `this.canvas` would read
//    the wrong region entirely, and pixels already composited by a prior
//    step. Fix: everything "under the glass" (void base, nebula, star
//    layers, drift objects, satellite/comet events, planets) is drawn every
//    frame into a dedicated OFFSCREEN, UNTRANSFORMED, world-space scene
//    canvas sized exactly renderer.width x renderer.height (analogous to
//    renderer._lightmapCanvas), fully redrawn from scratch each call (cheap
//    at 1600x540, never cached across frames). That scene canvas is then
//    composited onto the live ctx: the sky/void bands above laneY and below
//    laneBottom blit 1:1, and the lane band blits row-by-row with the same
//    sinusoidal offset technique the sandbox used, except now reading from
//    this distinct, always-in-sync source image - never the live
//    destination itself. Both the static bake and every dynamic frame reuse
//    the same drawVoidglassScene()/applyVoidglassRefraction() helpers.
//
// Known simplification (flagged for review): the scene canvas is a fixed
// 1600x540 world window, not padded for camera bounds the way sul/swamp's
// own static/dynamic canvases are. Star/nebula/planet/drift-object counts
// and positions ARE built across a padded field (buildLayout's padX=1200,
// matching backgroundSul.js's own recipe) so the density and wrap-seam
// stay correct within that 1600-wide window regardless of which slice of
// the padded field currently occupies it, and the void base gradient itself
// is separately filled across the FULL padded/camera bounds directly (no
// gaps, ever) - but an extreme zoom-out/pan that shows world x outside
// [0, 1600] will show that plain void gradient without the starfield out
// there. This mirrors _lightmapCanvas's own fixed-size, camera-independent
// world alignment and keeps the refraction fix simple; flagged in case a
// wider scene canvas is later deemed worth the extra per-frame cost.
//
// Also simplified: the sandbox's one-time "lightspeed jump" intro swoosh
// (state.jumpStart, tied to page load and a dev-only "replay jump" button)
// is dropped - cruise speed is a flat constant here, since jumpStart had no
// analogue in the real match lifecycle and wasn't in the port's requested
// scope (DOM control-panel wiring, loop()/rAF, FPS meter, drawGuides(),
// state.animate/showGuides/speedRange bindings, manual seed input).

function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function wrapCoord(value, span) {
  return ((value % span) + span) % span;
}

function resolveBounds(renderer, customBounds) {
  const bounds = customBounds || (renderer.viewport ? renderer.viewport.getVisibleBounds() : null);
  const rawTop = bounds ? (typeof bounds.top === 'number' ? bounds.top : (typeof bounds.visibleTop === 'number' ? bounds.visibleTop : -40)) : -40;
  const rawBottom = bounds ? (typeof bounds.bottom === 'number' ? bounds.bottom : (typeof bounds.visibleBottom === 'number' ? bounds.visibleBottom : (renderer.height + 40))) : (renderer.height + 40);
  const rawLeft = bounds ? (typeof bounds.left === 'number' ? bounds.left : (typeof bounds.visibleLeft === 'number' ? bounds.visibleLeft : -40)) : -40;
  const rawRight = bounds ? (typeof bounds.right === 'number' ? bounds.right : (typeof bounds.visibleRight === 'number' ? bounds.visibleRight : (renderer.width + 40))) : (renderer.width + 40);
  return {
    visibleTop: customBounds ? rawTop : Math.min(0, rawTop - 20),
    visibleBottom: customBounds ? rawBottom : Math.max(renderer.height, rawBottom + 20),
    visibleLeft: customBounds ? rawLeft : Math.min(0, rawLeft - 20),
    visibleRight: customBounds ? rawRight : Math.max(renderer.width, rawRight + 20)
  };
}

// Flat cruise speed (see header note - the sandbox's lightspeed-jump intro
// swoosh is not ported). CRUISE_SPEED_BASE is the reference px/s for a star
// at depthFactor 1 (the nearest layer); SPEED_MULTIPLIER matches the
// sandbox's own default slider value (a "steady hurtling pace").
const CRUISE_SPEED_BASE = 85;
const SPEED_MULTIPLIER = 7.0;
function travelled(t) {
  return CRUISE_SPEED_BASE * SPEED_MULTIPLIER * t;
}
const INSTANT_SPEED = CRUISE_SPEED_BASE * SPEED_MULTIPLIER;

// --- Shared, non-seed-dependent lazy sprite singletons --------------------
// Star tint color table and its two cached sprite sheets (streak + bloom).
// Neither depends on mapSeed, so - unlike the per-renderer layout below -
// these are simple shared module-level lazy singletons, same convention as
// getSulNebulaCanvas()/getTrafficStreakCanvas() in background.js: guarded by
// `typeof document !== 'undefined'` so importing this file under Node
// (tests/*.mjs's minimal document mock) never eagerly touches the DOM.
const STAR_TINTS = {
  white: { rgb: '226, 232, 240', glow: false },
  blue: { rgb: '147, 197, 253', glow: false },
  warm: { rgb: '252, 165, 116', glow: false },
  hot: { rgb: '224, 242, 255', glow: true }
};

let _streakSprites = null;
function getStreakSprite(tint) {
  if (!_streakSprites && typeof document !== 'undefined') {
    _streakSprites = {};
    const S_LEN = 256;
    const S_H = 16;
    for (const [tintName, tintObj] of Object.entries(STAR_TINTS)) {
      const cvs = document.createElement('canvas');
      cvs.width = S_LEN;
      cvs.height = S_H;
      const c = cvs.getContext('2d');
      const grad = c.createLinearGradient(0, 0, S_LEN, 0);
      grad.addColorStop(0, `rgba(${tintObj.rgb}, 1)`);
      grad.addColorStop(0.35, `rgba(${tintObj.rgb}, 0.55)`);
      grad.addColorStop(1, `rgba(${tintObj.rgb}, 0)`);
      c.fillStyle = grad;
      c.fillRect(0, 0, S_LEN, S_H);
      _streakSprites[tintName] = cvs;
    }
  }
  return _streakSprites ? _streakSprites[tint] : null;
}

let _bloomSprites = null;
function getBloomSprite(tint) {
  if (!_bloomSprites && typeof document !== 'undefined') {
    _bloomSprites = {};
    const S_SIZE = 40;
    const HALF = S_SIZE / 2;
    for (const [tintName, tintObj] of Object.entries(STAR_TINTS)) {
      const cvs = document.createElement('canvas');
      cvs.width = S_SIZE;
      cvs.height = S_SIZE;
      const c = cvs.getContext('2d');
      const grad = c.createRadialGradient(HALF, HALF, 0, HALF, HALF, HALF);
      grad.addColorStop(0, `rgba(${tintObj.rgb}, 0.55)`);
      grad.addColorStop(0.3, `rgba(${tintObj.rgb}, 0.24)`);
      grad.addColorStop(0.65, `rgba(${tintObj.rgb}, 0.06)`);
      grad.addColorStop(1, `rgba(${tintObj.rgb}, 0)`);
      c.fillStyle = grad;
      c.fillRect(0, 0, S_SIZE, S_SIZE);
      _bloomSprites[tintName] = cvs;
    }
  }
  return _bloomSprites ? _bloomSprites[tint] : null;
}

function pickTint(rng) {
  const r = rng();
  if (r > 0.94) return 'hot';
  if (r > 0.85) return 'warm';
  if (r > 0.65) return 'blue';
  return 'white';
}

// --- Seed-cached layout ----------------------------------------------------
// Pure function of (seed, width, height, laneY, laneH) - both online peers
// derive the identical starfield/scratch/frost layout from the identical
// server-issued mapSeed. Everything scroll-parallaxed (stars, nebula,
// planets, drift debris) is built across a padded field (padX=1200, padY=280)
// so density/wrap-seams stay correct regardless of which slice the scene
// canvas currently shows, including Mobile Portrait's 50% wide-zoom state
// where vertical world bounds reach y ≈ -180..+720.
const VOIDGLASS_PAD_Y = 280;

function buildStarLayer(rng, minX, totalFieldW, minY, totalFieldH, scale, { count, minR, maxR, depthMin, depthMax }) {
  const n = Math.round(count * scale);
  const arr = [];
  for (let i = 0; i < n; i++) {
    arr.push({
      x0: minX + rng() * totalFieldW,
      y: minY + rng() * totalFieldH,
      r: minR + rng() * (maxR - minR),
      depthFactor: depthMin + rng() * (depthMax - depthMin),
      tint: pickTint(rng),
      twinklePhase: rng() * Math.PI * 2
    });
  }
  return arr;
}

// A handful of stars that fly past IN FRONT of the lane (drawn in the
// dynamic-foreground pass, after units/particles), unlike far/mid/near which
// all sit "under the glass" behind everything. Deliberately sparse (see
// header note on drawForegroundStars) - this is a rare accent, not a second
// starfield, so a handful streaking past continuously reads as depth rather
// than clutter or occlusion spam.
function buildForegroundStars(rng, minX, totalFieldW, minY, totalFieldH, scaleY, { count, minR, maxR, depthMin, depthMax }) {
  const scaledCount = Math.max(count, Math.round(count * scaleY));
  const arr = [];
  for (let i = 0; i < scaledCount; i++) {
    arr.push({
      x0: minX + rng() * totalFieldW,
      y: minY + rng() * totalFieldH,
      r: minR + rng() * (maxR - minR),
      depthFactor: depthMin + rng() * (depthMax - depthMin),
      tint: pickTint(rng),
      twinklePhase: rng() * Math.PI * 2
    });
  }
  return arr;
}

function buildPlanets(rng, minX, totalFieldW, minY, totalFieldH, scale) {
  const arr = [];
  const baseCount = 3 + Math.floor(rng() * 3); // 3-5
  const count = Math.max(1, Math.round(baseCount * scale));
  for (let i = 0; i < count; i++) {
    arr.push({
      x0: minX + rng() * totalFieldW,
      y: minY + 20 + rng() * (totalFieldH - 40),
      r: 2 + rng() * 2.6,
      depthFactor: 0.02 + rng() * 0.025,
      hue: ['slate', 'rust', 'sand', 'ice'][Math.floor(rng() * 4)],
      hasRing: rng() < 0.3,
      ringTilt: (rng() - 0.5) * 0.7,
      shade: rng()
    });
  }
  return arr;
}

function buildScratches(rng, width, laneY, laneH) {
  const arr = [];
  const count = 55 + Math.floor(rng() * 20); // 55-74 scratches on the glass deck
  for (let i = 0; i < count; i++) {
    const typeRoll = rng();
    const x = rng() * width;
    const y = laneY + 6 + rng() * (laneH - 12);
    if (typeRoll < 0.35) {
      arr.push({ type: 'nick', x, y, len: 3 + rng() * 7, angle: rng() * Math.PI * 2, alpha: 0.02 + rng() * 0.045 });
    } else if (typeRoll < 0.65) {
      arr.push({ type: 'arc', x, y, len: 12 + rng() * 28, angle: rng() * Math.PI * 2, curve: (rng() - 0.5) * 10, alpha: 0.015 + rng() * 0.035 });
    } else if (typeRoll < 0.90) {
      arr.push({
        type: 'hairline',
        x,
        y,
        len: 20 + rng() * 65,
        angle: (rng() - 0.5) * 0.7 + (rng() > 0.5 ? 0 : Math.PI),
        alpha: 0.015 + rng() * 0.04,
        hasGlint: rng() > 0.7
      });
    } else {
      arr.push({ type: 'nick', x, y, len: 4 + rng() * 7, angle: rng() * Math.PI * 2, alpha: 0.025 + rng() * 0.04 });
      arr.push({
        type: 'nick',
        x: x + (rng() - 0.5) * 6,
        y: y + (rng() - 0.5) * 6,
        len: 3 + rng() * 5,
        angle: rng() * Math.PI * 2,
        alpha: 0.02 + rng() * 0.035
      });
    }
  }
  return arr;
}

function buildInternalFlaws(rng, width, laneY, laneH) {
  const arr = [];
  const bubbleCount = 14 + Math.floor(rng() * 7);
  for (let i = 0; i < bubbleCount; i++) {
    arr.push({
      type: 'bubble',
      x: 35 + rng() * (width - 70),
      y: laneY + 14 + rng() * (laneH - 28),
      r: 1.1 + rng() * 1.5,
      alpha: 0.28 + rng() * 0.35,
      dispersionAngle: rng() * Math.PI * 2
    });
  }
  const crackCount = 8 + Math.floor(rng() * 5);
  for (let i = 0; i < crackCount; i++) {
    const angle = (rng() - 0.5) * 1.6;
    const len = 5 + rng() * 9;
    arr.push({
      type: 'stress',
      x: 45 + rng() * (width - 90),
      y: laneY + 16 + rng() * (laneH - 32),
      angle,
      len,
      forkLen: 2.5 + rng() * 4.0,
      forkAngle: angle + (rng() > 0.5 ? 0.65 : -0.65),
      alpha: 0.24 + rng() * 0.28
    });
  }
  return arr;
}

function buildFrostPatches(rng, width, laneY, laneH) {
  const arr = [];
  const count = 3 + Math.floor(rng() * 3);
  for (let i = 0; i < count; i++) {
    arr.push({ x: rng() * width, y: laneY + rng() * laneH, r: 60 + rng() * 90, alpha: 0.03 + rng() * 0.035 });
  }
  return arr;
}

function buildReflectionField(rng, width, laneY, laneH) {
  const arr = [];
  const count = 16;
  for (let i = 0; i < count; i++) {
    arr.push({
      x0: rng() * width,
      y: laneY + 4 + rng() * (laneH - 8),
      r: 0.5 + rng() * 0.8,
      depthFactor: 0.8 + rng() * 0.4,
      tint: pickTint(rng)
    });
  }
  return arr;
}

function buildDriftObjects(rng, minX, totalFieldW, minY, totalFieldH, scale) {
  const arr = [];
  const baseCount = 2 + Math.floor(rng() * 2); // 2-3
  const count = Math.max(1, Math.round(baseCount * scale));
  for (let i = 0; i < count; i++) {
    arr.push({
      kind: 'debris',
      x0: minX + rng() * totalFieldW,
      y: minY + 20 + rng() * (totalFieldH - 40),
      depthFactor: 0.2 + rng() * 0.35,
      r: 3 + rng() * 4,
      spin: (rng() - 0.5) * 1.2,
      spinPhase: rng() * Math.PI * 2,
      shape: Math.floor(rng() * 3)
    });
  }
  return arr;
}

const NEBULA_PALETTES = [
  { core: '251, 191, 36', rim: '45, 212, 191' },
  { core: '244, 63, 94', rim: '96, 165, 250' },
  { core: '251, 146, 60', rim: '167, 139, 250' }
];

function buildNebulaDust(rng, minX, totalFieldW, minY, totalFieldH, scale) {
  const arr = [];
  const baseCount = 3 + Math.floor(rng() * 2); // 3-4
  const count = Math.max(1, Math.round(baseCount * scale));
  for (let i = 0; i < count; i++) {
    const palette = NEBULA_PALETTES[Math.floor(rng() * NEBULA_PALETTES.length)];
    const hazeR = 130 + rng() * 130;
    const wisps = [];
    const wispCount = 2 + Math.floor(rng() * 2);
    for (let w = 0; w < wispCount; w++) {
      wisps.push({
        dx: (rng() - 0.5) * hazeR * 1.1,
        dy: (rng() - 0.5) * hazeR * 0.7,
        r: hazeR * (0.22 + rng() * 0.22),
        useCore: rng() > 0.5
      });
    }
    arr.push({
      x0: minX + rng() * totalFieldW,
      y: minY + 30 + rng() * (totalFieldH - 60),
      hazeR,
      coreR: hazeR * (0.4 + rng() * 0.15),
      coreOffset: { dx: (rng() - 0.5) * hazeR * 0.3, dy: (rng() - 0.5) * hazeR * 0.3 },
      // Ultra-slow cosmic drift (~9px/min at cruise) - almost imperceptible
      // in real time, but steadily creeping, hence dynamic-only (never
      // frozen into the static bake).
      depthFactor: 0.00015 + rng() * 0.0001,
      palette,
      alpha: 0.05 + rng() * 0.035,
      wisps
    });
  }
  return arr;
}

function prerenderNebulae(nebulaDust) {
  if (typeof document === 'undefined') return;
  for (const n of nebulaDust) {
    const size = Math.ceil(n.hazeR * 2 + 40);
    const cvs = document.createElement('canvas');
    cvs.width = size;
    cvs.height = size;
    const c = cvs.getContext('2d');
    const cx = size / 2;
    const cy = size / 2;

    const haze = c.createRadialGradient(cx, cy, 0, cx, cy, n.hazeR);
    haze.addColorStop(0, `rgba(${n.palette.rim}, ${n.alpha})`);
    haze.addColorStop(1, `rgba(${n.palette.rim}, 0)`);
    c.fillStyle = haze;
    c.beginPath();
    c.arc(cx, cy, n.hazeR, 0, Math.PI * 2);
    c.fill();

    const coreX = cx + n.coreOffset.dx;
    const coreY = cy + n.coreOffset.dy;
    const core = c.createRadialGradient(coreX, coreY, 0, coreX, coreY, n.coreR);
    core.addColorStop(0, `rgba(${n.palette.core}, ${n.alpha * 1.3})`);
    core.addColorStop(1, `rgba(${n.palette.core}, 0)`);
    c.fillStyle = core;
    c.beginPath();
    c.arc(coreX, coreY, n.coreR, 0, Math.PI * 2);
    c.fill();

    for (const w of n.wisps) {
      const rgb = w.useCore ? n.palette.core : n.palette.rim;
      const wx = cx + w.dx;
      const wy = cy + w.dy;
      const wisp = c.createRadialGradient(wx, wy, 0, wx, wy, w.r);
      wisp.addColorStop(0, `rgba(${rgb}, ${n.alpha * 0.9})`);
      wisp.addColorStop(1, `rgba(${rgb}, 0)`);
      c.fillStyle = wisp;
      c.beginPath();
      c.arc(wx, wy, w.r, 0, Math.PI * 2);
      c.fill();
    }

    n.canvas = cvs;
    n.size = size;
  }
}

function prerenderScratches(scratches, width, laneH) {
  if (typeof document === 'undefined') return null;
  const cvs = document.createElement('canvas');
  cvs.width = width;
  cvs.height = laneH;
  const sc = cvs.getContext('2d');
  sc.lineCap = 'round';
  for (const s of scratches) {
    sc.strokeStyle = `rgba(200, 225, 255, ${s.alpha})`;
    sc.lineWidth = s.type === 'nick' ? 0.9 : 0.65;
    sc.beginPath();
    const localY = s.y - (s.laneY !== undefined ? s.laneY : 0);
    if (s.type === 'arc') {
      const ex = s.x + Math.cos(s.angle) * s.len;
      const ey = localY + Math.sin(s.angle) * s.len;
      const mx = (s.x + ex) / 2 + Math.cos(s.angle + Math.PI / 2) * s.curve;
      const my = (localY + ey) / 2 + Math.sin(s.angle + Math.PI / 2) * s.curve;
      sc.moveTo(s.x, localY);
      sc.quadraticCurveTo(mx, my, ex, ey);
    } else {
      sc.moveTo(s.x, localY);
      sc.lineTo(s.x + Math.cos(s.angle) * s.len, localY + Math.sin(s.angle) * s.len);
    }
    sc.stroke();
    if (s.hasGlint) {
      sc.fillStyle = `rgba(240, 250, 255, ${s.alpha * 1.6})`;
      sc.beginPath();
      sc.arc(s.x + Math.cos(s.angle) * (s.len * 0.5), localY + Math.sin(s.angle) * (s.len * 0.5), 0.7, 0, Math.PI * 2);
      sc.fill();
    }
  }
  return cvs;
}

function prerenderFrostPatches(frostPatches, width, laneH) {
  if (typeof document === 'undefined') return null;
  const cvs = document.createElement('canvas');
  cvs.width = width;
  cvs.height = laneH;
  const fc = cvs.getContext('2d');
  for (const f of frostPatches) {
    const localY = f.y - (f.laneY !== undefined ? f.laneY : 0);
    const grad = fc.createRadialGradient(f.x, localY, 0, f.x, localY, f.r);
    grad.addColorStop(0, `rgba(220, 235, 245, ${f.alpha})`);
    grad.addColorStop(1, 'rgba(220, 235, 245, 0)');
    fc.fillStyle = grad;
    fc.beginPath();
    fc.arc(f.x, localY, f.r, 0, Math.PI * 2);
    fc.fill();
  }
  return cvs;
}

function prerenderInternalFlaws(internalFlaws, width, laneH) {
  if (typeof document === 'undefined') return null;
  const cvs = document.createElement('canvas');
  cvs.width = width;
  cvs.height = laneH;
  const fc = cvs.getContext('2d');
  for (const f of internalFlaws) {
    const localY = f.y - (f.laneY !== undefined ? f.laneY : 0);
    if (f.type === 'bubble') {
      fc.strokeStyle = `rgba(2, 6, 14, ${f.alpha * 0.9})`;
      fc.lineWidth = 0.7;
      fc.beginPath();
      fc.arc(f.x, localY, f.r, 0, Math.PI * 2);
      fc.stroke();
      fc.fillStyle = `rgba(180, 220, 245, ${f.alpha * 0.45})`;
      fc.beginPath();
      fc.arc(f.x + f.r * 0.25, localY + f.r * 0.25, f.r * 0.75, 0, Math.PI * 2);
      fc.fill();
      fc.fillStyle = `rgba(255, 255, 255, ${f.alpha * 0.95})`;
      fc.beginPath();
      fc.arc(f.x - f.r * 0.35, localY - f.r * 0.35, Math.max(0.4, f.r * 0.25), 0, Math.PI * 2);
      fc.fill();
    } else if (f.type === 'stress') {
      const x2 = f.x + Math.cos(f.angle) * f.len;
      const y2 = localY + Math.sin(f.angle) * f.len;
      const fx2 = f.x + Math.cos(f.forkAngle) * f.forkLen;
      const fy2 = localY + Math.sin(f.forkAngle) * f.forkLen;
      fc.strokeStyle = `rgba(2, 8, 18, ${f.alpha * 0.75})`;
      fc.lineWidth = 0.8;
      fc.beginPath();
      fc.moveTo(f.x + 0.4, localY + 0.4);
      fc.lineTo(x2 + 0.4, y2 + 0.4);
      fc.moveTo(f.x + 0.4, localY + 0.4);
      fc.lineTo(fx2 + 0.4, fy2 + 0.4);
      fc.stroke();
      fc.strokeStyle = `rgba(215, 240, 255, ${f.alpha * 0.85})`;
      fc.lineWidth = 0.5;
      fc.beginPath();
      fc.moveTo(f.x, localY);
      fc.lineTo(x2, y2);
      fc.moveTo(f.x, localY);
      fc.lineTo(fx2, fy2);
      fc.stroke();
    }
  }
  return cvs;
}

function buildLayout(seed, width, height, laneY, laneH) {
  const rng = mulberry32(seed);
  const padX = 1200;
  const minX = -padX;
  const maxX = width + padX;
  const totalFieldW = maxX - minX;
  const scale = totalFieldW / width;

  const padY = VOIDGLASS_PAD_Y;
  const minY = -padY;
  const maxY = height + padY;
  const totalFieldH = maxY - minY;
  const scaleY = totalFieldH / height;
  const areaScale = scale * scaleY;
  const sparseScale = scale * (1 + (scaleY - 1) * 0.6);

  const farStars = buildStarLayer(rng, minX, totalFieldW, minY, totalFieldH, areaScale, { count: 190, minR: 0.15, maxR: 0.4, depthMin: 0.06, depthMax: 0.16 });
  const midStars = buildStarLayer(rng, minX, totalFieldW, minY, totalFieldH, areaScale, { count: 90, minR: 0.3, maxR: 0.65, depthMin: 0.28, depthMax: 0.5 });
  const nearStars = buildStarLayer(rng, minX, totalFieldW, minY, totalFieldH, areaScale, { count: 36, minR: 0.5, maxR: 0.95, depthMin: 0.75, depthMax: 1.15 });
  const fgStars = buildForegroundStars(rng, minX, totalFieldW, minY, totalFieldH, scaleY, { count: 3, minR: 1.6, maxR: 2.6, depthMin: 1.6, depthMax: 2.2 });
  const fgStars2 = buildForegroundStars(rng, minX, totalFieldW, minY, totalFieldH, scaleY, { count: 3, minR: 1.1, maxR: 1.8, depthMin: 1.6, depthMax: 2.2 });
  const planets = buildPlanets(rng, minX, totalFieldW, minY, totalFieldH, sparseScale);

  const scratches = buildScratches(rng, width, laneY, laneH);
  for (const s of scratches) s.laneY = laneY;
  const scratchesCanvas = prerenderScratches(scratches, width, laneH);

  const internalFlaws = buildInternalFlaws(rng, width, laneY, laneH);
  for (const f of internalFlaws) f.laneY = laneY;
  const flawsCanvas = prerenderInternalFlaws(internalFlaws, width, laneH);

  const frostPatches = buildFrostPatches(rng, width, laneY, laneH);
  for (const f of frostPatches) f.laneY = laneY;
  const frostCanvas = prerenderFrostPatches(frostPatches, width, laneH);

  const reflectionField = buildReflectionField(rng, width, laneY, laneH);
  const driftObjects = buildDriftObjects(rng, minX, totalFieldW, minY, totalFieldH, sparseScale);
  const nebulaDust = buildNebulaDust(rng, minX, totalFieldW, minY, totalFieldH, sparseScale);
  prerenderNebulae(nebulaDust);

  return {
    minX,
    maxX,
    totalFieldW,
    minY,
    maxY,
    totalFieldH,
    farStars,
    midStars,
    nearStars,
    fgStars,
    fgStars2,
    planets,
    scratches,
    scratchesCanvas,
    internalFlaws,
    flawsCanvas,
    frostPatches,
    frostCanvas,
    reflectionField,
    driftObjects,
    nebulaDust
  };
}

function getLayout(renderer, battlefield) {
  const laneY = battlefield.laneY;
  const laneH = battlefield.laneHeight;
  const seed = battlefield.mapSeed || 0;
  const key = `${seed}:${laneY}:${laneH}:${renderer.width}`;
  if (renderer._voidglassLayout && renderer._voidglassLayoutKey === key) {
    return renderer._voidglassLayout;
  }
  renderer._voidglassLayout = buildLayout(seed, renderer.width, renderer.height, laneY, laneH);
  renderer._voidglassLayoutKey = key;
  return renderer._voidglassLayout;
}

// --- Offscreen "under glass" scene canvas ----------------------------------
// Lazy per-renderer singleton (analogous to renderer._lightmapCanvas), fully
// redrawn every call - never cached across frames. Vertically padded by
// VOIDGLASS_PAD_Y above and below [0, renderer.height] so Mobile Portrait's
// 50% wide-zoom view has full starfield/nebula/planet coverage above and below.
function getSceneCanvas(renderer) {
  const targetH = renderer.height + VOIDGLASS_PAD_Y * 2;
  if ((!renderer._voidglassSceneCanvas || renderer._voidglassSceneCanvas.height !== targetH) && typeof document !== 'undefined') {
    renderer._voidglassSceneCanvas = document.createElement('canvas');
    renderer._voidglassSceneCanvas.width = renderer.width;
    renderer._voidglassSceneCanvas.height = targetH;
    renderer._voidglassSceneCtx = renderer._voidglassSceneCanvas.getContext('2d');
  }
  return renderer._voidglassSceneCanvas;
}

// --- "Under glass" drawing primitives (operate on any 2D ctx) -------------

let _voidBaseGrad = null;
let _voidBaseH = -1;
let _voidBaseMinY = 0;

function drawVoidBase(ctx, width, height, minY = -VOIDGLASS_PAD_Y, totalH = height + VOIDGLASS_PAD_Y * 2) {
  if (!_voidBaseGrad || _voidBaseH !== totalH || _voidBaseMinY !== minY) {
    _voidBaseGrad = ctx.createLinearGradient(0, minY, 0, minY + totalH);
    _voidBaseGrad.addColorStop(0, '#02030a');
    _voidBaseGrad.addColorStop(0.5, '#03040c');
    _voidBaseGrad.addColorStop(1, '#010206');
    _voidBaseH = totalH;
    _voidBaseMinY = minY;
  }
  ctx.fillStyle = _voidBaseGrad;
  ctx.fillRect(0, minY, width, totalH);
}

function drawNebulaDust(ctx, nebulaDust, dist, minX, totalFieldW) {
  ctx.save();
  ctx.globalCompositeOperation = 'screen';
  for (const n of nebulaDust) {
    if (!n.canvas) continue;
    const x = wrapCoord(n.x0 - minX - dist * n.depthFactor, totalFieldW) + minX;
    ctx.drawImage(n.canvas, Math.round(x - n.size / 2), Math.round(n.y - n.size / 2));
  }
  ctx.restore();
}

function drawStarDot(ctx, x, y, r, tint, alpha, laneY, laneBottom, quality) {
  const t = STAR_TINTS[tint];
  if (quality !== 'low' && y >= laneY && y <= laneBottom && (r >= 0.85 || t.glow)) {
    const bloom = getBloomSprite(tint);
    if (bloom) ctx.drawImage(bloom, Math.round(x - 20), Math.round(y - 20));
  }
  ctx.fillStyle = `rgba(${t.rgb}, ${alpha})`;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
  if (t.glow && (y < laneY || y > laneBottom)) {
    const bloom = getBloomSprite(tint);
    if (bloom) ctx.drawImage(bloom, Math.round(x - 20), Math.round(y - 20));
  }
}

function drawStarStreak(ctx, x, y, r, tint, alpha, len, laneY, laneBottom, quality) {
  const sprite = getStreakSprite(tint);
  if (!sprite) return;
  const clampedLen = Math.min(256, Math.max(2, Math.round(len)));
  const h = Math.max(1.0, Math.round(r * 1.5));
  if (quality !== 'low' && y >= laneY && y <= laneBottom && r >= 1.1) {
    const bloom = getBloomSprite(tint);
    if (bloom) ctx.drawImage(bloom, Math.round(x - 20), Math.round(y - 20));
  }
  // Bright head pinned exactly at (x, y) - same anchor drawStarDot() uses -
  // fading tail trails toward +x (where the star was a moment ago, since
  // travel is in the -x direction / leftward). Source sampled from the
  // sprite's bright end (0) so the head stays fully lit regardless of len.
  ctx.globalAlpha = alpha;
  ctx.drawImage(sprite, 0, 0, clampedLen, 16, Math.round(x), Math.round(y - h / 2), clampedLen, h);
  ctx.globalAlpha = 1.0;
}

// Medium tier draws every other star in each layer instead of the full set
// (Paradigm 10: Multi-Tier Quality-Gating), same "index += 2" thinning
// convention as backgroundSwamp.js's own medium-tier strand/foam-tongue
// thinning - high/ultra/low star counts are unchanged (low already sheds the
// bloom/twinkle/streak cost per-star via drawStarDot()/drawStarStreak()'s own
// quality checks, so it doesn't additionally need fewer stars).
function drawStarLayerFar(ctx, list, t, dist, minX, totalFieldW, laneY, laneBottom, quality) {
  const step = quality === 'medium' ? 2 : 1;
  for (let i = 0; i < list.length; i += step) {
    const s = list[i];
    const x = wrapCoord(s.x0 - minX - dist * s.depthFactor, totalFieldW) + minX;
    const twinkle = quality === 'low' ? 1 : 0.55 + 0.45 * Math.sin(t * 1.6 + s.twinklePhase);
    drawStarDot(ctx, x, s.y, s.r, s.tint, 0.7 * twinkle, laneY, laneBottom, quality);
  }
}

function drawStarLayerMid(ctx, list, t, dist, speedNow, minX, totalFieldW, laneY, laneBottom, quality) {
  const streaking = quality !== 'low' && speedNow > CRUISE_SPEED_BASE * 1.4;
  const step = quality === 'medium' ? 2 : 1;
  for (let i = 0; i < list.length; i += step) {
    const s = list[i];
    const x = wrapCoord(s.x0 - minX - dist * s.depthFactor, totalFieldW) + minX;
    if (streaking) {
      drawStarStreak(ctx, x, s.y, s.r, s.tint, 0.75, Math.min(60, speedNow * s.depthFactor * 0.12), laneY, laneBottom, quality);
    } else {
      const twinkle = quality === 'low' ? 1 : 0.6 + 0.4 * Math.sin(t * 1.8 + s.twinklePhase);
      drawStarDot(ctx, x, s.y, s.r, s.tint, 0.8 * twinkle, laneY, laneBottom, quality);
    }
  }
}

function drawStarLayerNear(ctx, list, dist, speedNow, minX, totalFieldW, laneY, laneBottom, quality) {
  const streak = quality !== 'low';
  const step = quality === 'medium' ? 2 : 1;
  for (let i = 0; i < list.length; i += step) {
    const s = list[i];
    const x = wrapCoord(s.x0 - minX - dist * s.depthFactor, totalFieldW) + minX;
    if (streak) {
      const len = Math.max(4, Math.min(140, speedNow * s.depthFactor * 0.16));
      drawStarStreak(ctx, x, s.y, s.r, s.tint, 0.9, len, laneY, laneBottom, quality);
    } else {
      drawStarDot(ctx, x, s.y, s.r, s.tint, 0.9, laneY, laneBottom, quality);
    }
  }
}

// Foreground-pass counterpart to drawStarLayerNear() above, but drawn on top
// of units/particles instead of under the glass. Kept deliberately sparse
// (buildForegroundStars() is called once per foreground layer, 3 stars each)
// - the point is a few stars continuously streaking past close to camera,
// occasionally clipping a unit silhouette, never a dense second field
// competing with the battlefield. Two layers (layout.fgStars/fgStars2) share
// this same draw function, differing only in the size range they were built
// with, so the foreground itself reads with a bit of size depth too.
// laneY/laneBottom are passed as the full canvas height so drawStarStreak's
// in-lane/out-of-lane bloom branch treats the whole frame uniformly, since
// these stars roam the full height, not just the lane band.
function drawForegroundStars(ctx, list, t, dist, speedNow, minX, totalFieldW, width, height, quality) {
  const minY = -VOIDGLASS_PAD_Y;
  const maxY = height + VOIDGLASS_PAD_Y;
  for (const s of list) {
    const x = wrapCoord(s.x0 - minX - dist * s.depthFactor, totalFieldW) + minX;
    if (x < -40 || x > width + 40) continue;
    const twinkle = quality === 'low' ? 1 : 0.7 + 0.3 * Math.sin(t * 2.1 + s.twinklePhase);
    const len = Math.max(14, Math.min(200, speedNow * s.depthFactor * 0.16));
    drawStarStreak(ctx, x, s.y, s.r, s.tint, 0.95 * twinkle, len, minY, maxY, quality);
  }
}

function planetHueStops(hue) {
  switch (hue) {
    case 'rust': return ['rgba(196, 120, 90, 0.9)', 'rgba(96, 52, 38, 0.92)'];
    case 'sand': return ['rgba(206, 184, 140, 0.9)', 'rgba(110, 92, 58, 0.92)'];
    case 'ice': return ['rgba(180, 214, 230, 0.9)', 'rgba(70, 100, 116, 0.92)'];
    default: return ['rgba(150, 156, 170, 0.9)', 'rgba(66, 70, 82, 0.92)'];
  }
}

// Cached per-hue planet gradient sprites. buildPlanets() only ever produces
// 4 distinct hues (slate/rust/sand/ice - see planetHueStops()'s switch), so
// - same lazy-singleton idiom as getBloomSprite()/getStreakSprite() above -
// each hue's radial gradient is baked exactly once at a fixed reference
// radius into its own small offscreen canvas, never createRadialGradient()'d
// again. Per-planet position and radius are then applied purely via
// drawImage()'s destination rect (translate + uniform scale), which
// reproduces the same relative gradient shape a fresh native gradient at
// that radius would have drawn (a radial gradient's stops are defined as
// fractions of its own radius, so scaling the whole raster uniformly
// preserves them) - up to 5 createRadialGradient() calls/frame become 0.
const PLANET_HUES = ['slate', 'rust', 'sand', 'ice'];
const PLANET_SPRITE_R = 32; // reference radius the sprite is baked at
let _planetSprites = null;
function getPlanetSprite(hue) {
  if (!_planetSprites && typeof document !== 'undefined') {
    _planetSprites = {};
    const size = PLANET_SPRITE_R * 2;
    for (const hueName of PLANET_HUES) {
      const cvs = document.createElement('canvas');
      cvs.width = size;
      cvs.height = size;
      const c = cvs.getContext('2d');
      const [lit, shadow] = planetHueStops(hueName);
      const grad = c.createRadialGradient(
        PLANET_SPRITE_R - PLANET_SPRITE_R * 0.3, PLANET_SPRITE_R - PLANET_SPRITE_R * 0.3, 0.1,
        PLANET_SPRITE_R, PLANET_SPRITE_R, PLANET_SPRITE_R
      );
      grad.addColorStop(0, lit);
      grad.addColorStop(1, shadow);
      c.fillStyle = grad;
      c.beginPath();
      c.arc(PLANET_SPRITE_R, PLANET_SPRITE_R, PLANET_SPRITE_R, 0, Math.PI * 2);
      c.fill();
      _planetSprites[hueName] = cvs;
    }
  }
  return _planetSprites ? _planetSprites[hue] : null;
}

function drawPlanets(ctx, planets, dist, minX, totalFieldW, width, quality) {
  for (const p of planets) {
    const x = wrapCoord(p.x0 - minX - dist * p.depthFactor, totalFieldW) + minX;
    if (x < -10 || x > width + 10) continue;
    if (p.hasRing && quality !== 'low') {
      ctx.save();
      ctx.strokeStyle = 'rgba(214, 224, 236, 0.35)';
      ctx.lineWidth = 0.6;
      ctx.beginPath();
      ctx.ellipse(x, p.y, p.r * 1.9, p.r * 0.55, p.ringTilt, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }
    const sprite = getPlanetSprite(p.hue);
    if (sprite) {
      ctx.drawImage(sprite, x - p.r, p.y - p.r, p.r * 2, p.r * 2);
    } else {
      // No document (headless/test environment without canvas support) -
      // fall back to the direct native gradient so behavior is unchanged.
      const [lit, shadow] = planetHueStops(p.hue);
      const grad = ctx.createRadialGradient(x - p.r * 0.3, p.y - p.r * 0.3, 0.1, x, p.y, p.r);
      grad.addColorStop(0, lit);
      grad.addColorStop(1, shadow);
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(x, p.y, p.r, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

function drawSatellite(ctx, x, y, scale, t, phase, rot) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rot);
  ctx.scale(scale, scale);
  ctx.fillStyle = 'rgba(80, 120, 190, 0.85)';
  ctx.fillRect(-14, -1.2, 8, 2.4);
  ctx.fillRect(6, -1.2, 8, 2.4);
  ctx.strokeStyle = 'rgba(220, 230, 240, 0.5)';
  ctx.lineWidth = 0.4;
  ctx.strokeRect(-14, -1.2, 8, 2.4);
  ctx.strokeRect(6, -1.2, 8, 2.4);
  ctx.strokeStyle = 'rgba(200, 210, 220, 0.7)';
  ctx.beginPath();
  ctx.moveTo(0, -2);
  ctx.lineTo(0, -6);
  ctx.stroke();
  ctx.fillStyle = 'rgba(180, 190, 200, 0.9)';
  ctx.fillRect(-3, -2, 6, 4);
  const blink = 0.5 + 0.5 * Math.sin(t * 4 + phase);
  ctx.fillStyle = `rgba(255, 80, 80, ${0.4 + 0.5 * blink})`;
  ctx.beginPath();
  ctx.arc(3, -2, 0.8, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawDebris(ctx, x, y, r, rot, shape) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rot);
  ctx.fillStyle = 'rgba(90, 90, 100, 0.85)';
  ctx.beginPath();
  if (shape === 0) {
    ctx.moveTo(-r, -r * 0.6);
    ctx.lineTo(r * 0.8, -r);
    ctx.lineTo(r, r * 0.5);
    ctx.lineTo(-r * 0.4, r);
    ctx.closePath();
  } else if (shape === 1) {
    ctx.moveTo(-r, 0);
    ctx.lineTo(0, -r);
    ctx.lineTo(r, 0);
    ctx.lineTo(0, r * 0.7);
    ctx.closePath();
  } else {
    ctx.arc(0, 0, r, 0, Math.PI * 2);
  }
  ctx.fill();
  ctx.strokeStyle = 'rgba(160, 160, 170, 0.3)';
  ctx.lineWidth = 0.5;
  ctx.stroke();
  ctx.restore();
}

function drawDriftObjects(ctx, driftObjects, t, dist, minX, totalFieldW, width) {
  for (const o of driftObjects) {
    const x = wrapCoord(o.x0 - minX - dist * o.depthFactor, totalFieldW) + minX;
    if (x < -20 || x > width + 20) continue;
    drawDebris(ctx, x, o.y, o.r, o.spinPhase + t * o.spin, o.shape);
  }
}

const SATELLITE_CYCLE = 360; // 6 minutes
const SATELLITE_ACTIVE = 9;

function drawSatelliteEvent(ctx, t, offset, width, height) {
  const cyclePos = (t + offset) % SATELLITE_CYCLE;
  if (cyclePos > SATELLITE_ACTIVE) return;
  const progress = cyclePos / SATELLITE_ACTIVE;
  const cycleIndex = Math.floor((t + offset) / SATELLITE_CYCLE);
  const rng = mulberry32(cycleIndex * 50331653 + 13);
  const y = 30 + rng() * (height - 60);
  const dir = rng() > 0.5 ? 1 : -1;
  const scale = 0.8 + rng() * 0.5;
  const rot = rng() * Math.PI * 2;
  const blinkPhase = rng() * Math.PI * 2;
  const x = dir === 1 ? -40 + progress * (width + 80) : width + 40 - progress * (width + 80);
  drawSatellite(ctx, x, y, scale, t, blinkPhase, rot);
}

const COMET_CYCLE = 46;
const COMET_ACTIVE = 3.0;

let _voidglassCometGlowCanvas = null;
function getVoidglassCometGlowCanvas() {
  if (typeof document === 'undefined') return null;
  if (!_voidglassCometGlowCanvas) {
    _voidglassCometGlowCanvas = document.createElement('canvas');
    _voidglassCometGlowCanvas.width = 32;
    _voidglassCometGlowCanvas.height = 32;
    const cctx = _voidglassCometGlowCanvas.getContext('2d');
    const glow = cctx.createRadialGradient(16, 16, 0, 16, 16, 16);
    glow.addColorStop(0, 'rgba(235, 248, 255, 0.45)');
    glow.addColorStop(0.4, 'rgba(180, 225, 255, 0.16)');
    glow.addColorStop(1, 'rgba(180, 225, 255, 0)');
    cctx.fillStyle = glow;
    cctx.beginPath();
    cctx.arc(16, 16, 16, 0, Math.PI * 2);
    cctx.fill();
  }
  return _voidglassCometGlowCanvas;
}

let _cometStrandGrad = null;
function getCometStrandGrad(ctx) {
  if (!_cometStrandGrad) {
    _cometStrandGrad = ctx.createLinearGradient(-200, 0, 0, 0);
    _cometStrandGrad.addColorStop(0, 'rgba(170, 220, 255, 0)');
    _cometStrandGrad.addColorStop(0.45, 'rgba(185, 230, 255, 0.5)');
    _cometStrandGrad.addColorStop(1, 'rgba(235, 248, 255, 1)');
  }
  return _cometStrandGrad;
}

function drawComet(ctx, t, offset, width, height) {
  const cyclePos = (t + offset) % COMET_CYCLE;
  if (cyclePos > COMET_ACTIVE) return;
  const cycleIndex = Math.floor((t + offset) / COMET_CYCLE);
  const rng = mulberry32(cycleIndex * 104729 + 7);
  const startX = -60 + rng() * 280;
  const startY = 25 + rng() * (height * 0.65);
  const angle = 0.14 + rng() * 0.22;
  const travelPx = 860 + rng() * 260;
  const tailLen = 170 + rng() * 80;
  const curve = (rng() - 0.5) * 26;

  const progress = cyclePos / COMET_ACTIVE;
  const fade = progress < 0.1 ? progress / 0.1 : progress > 0.82 ? (1 - progress) / 0.18 : 1;
  const headX = startX + Math.cos(angle) * travelPx * progress;
  const headY = startY + Math.sin(angle) * travelPx * progress;
  const tailX = headX - Math.cos(angle) * tailLen;
  const tailY = headY - Math.sin(angle) * tailLen;

  ctx.save();

  ctx.strokeStyle = `rgba(180, 230, 255, ${0.11 * fade})`;
  ctx.lineWidth = 14;
  ctx.lineCap = 'round';
  ctx.beginPath();
  const midWiggle = Math.sin(t * 3.5) * 8;
  const midX = (tailX + headX) / 2 + Math.cos(angle + Math.PI / 2) * (curve + midWiggle);
  const midY = (tailY + headY) / 2 + Math.sin(angle + Math.PI / 2) * (curve + midWiggle);
  ctx.moveTo(tailX, tailY);
  ctx.quadraticCurveTo(midX, midY, headX, headY);
  ctx.stroke();

  const strandCount = 3;
  const sGrad = getCometStrandGrad(ctx);
  for (let sIdx = 0; sIdx < strandCount; sIdx++) {
    const strandPhase = sIdx * 2.1;
    const strandWidth = sIdx === 0 ? 2.2 : 1.3;
    const strandAlpha = sIdx === 0 ? 0.30 : 0.16;

    ctx.save();
    ctx.translate(headX, headY);
    ctx.rotate(angle);
    ctx.globalAlpha = strandAlpha * fade;
    ctx.strokeStyle = sGrad;
    ctx.lineWidth = strandWidth;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    const steps = 14;
    for (let step = 1; step <= steps; step++) {
      const frac = step / steps;
      const wave = Math.sin(t * 7.5 - frac * 11.0 + strandPhase) * (frac * 13) +
                   Math.sin(t * 13.0 - frac * 17.0 + strandPhase * 0.5) * (frac * 5);
      const lat = curve * frac + wave;
      ctx.lineTo(-tailLen * frac, lat);
    }
    ctx.stroke();
    ctx.restore();
  }

  for (let i = 0; i < 16; i++) {
    const flow = ((t * 1.8 + i * 0.19) % 1.0);
    const spread = Math.sin(i * 37.1 + t * 4) * (flow * 14);
    const px = headX - Math.cos(angle) * (tailLen * flow) + Math.cos(angle + Math.PI / 2) * spread;
    const py = headY - Math.sin(angle) * (tailLen * flow) + Math.sin(angle + Math.PI / 2) * spread;
    const pAlpha = (1 - flow) * 0.35 * fade;
    const rgb = i % 4 === 0 ? '170, 255, 220' : '220, 240, 255';
    ctx.fillStyle = `rgba(${rgb}, ${pAlpha})`;
    ctx.beginPath();
    ctx.arc(px, py, 0.6 + (1 - flow) * 0.8, 0, Math.PI * 2);
    ctx.fill();
  }

  const cgCanvas = getVoidglassCometGlowCanvas();
  if (cgCanvas) {
    ctx.save();
    ctx.globalAlpha = fade;
    ctx.drawImage(cgCanvas, headX - 16, headY - 16);
    ctx.restore();
  }

  ctx.fillStyle = `rgba(255, 255, 255, ${0.85 * fade})`;
  ctx.beginPath();
  ctx.arc(headX, headY, 2.0, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

// Draws everything "under the glass" (from drawVoidBase() through
// drawComet() in the sandbox's render(t)) into a fresh offscreen scene ctx.
// cometOffset/satelliteOffset are the renderer-cached Math.random() cycle
// offsets (see the caller methods below) - passing 0 (as the static bake
// does) simply freezes those rare events at their t=0 phase, same as every
// other frozen static-bake element.
function drawVoidglassScene(sceneCtx, layout, width, height, t, dist, speedNow, laneY, laneBottom, quality, cometOffset, satelliteOffset) {
  drawVoidBase(sceneCtx, width, height);
  if (quality !== 'low') drawNebulaDust(sceneCtx, layout.nebulaDust, dist, layout.minX, layout.totalFieldW);
  drawStarLayerFar(sceneCtx, layout.farStars, t, dist, layout.minX, layout.totalFieldW, laneY, laneBottom, quality);
  drawStarLayerMid(sceneCtx, layout.midStars, t, dist, speedNow, layout.minX, layout.totalFieldW, laneY, laneBottom, quality);
  if (quality !== 'low') drawDriftObjects(sceneCtx, layout.driftObjects, t, dist, layout.minX, layout.totalFieldW, width);
  if (quality !== 'low') drawSatelliteEvent(sceneCtx, t, satelliteOffset, width, height);
  drawPlanets(sceneCtx, layout.planets, dist, layout.minX, layout.totalFieldW, width, quality);
  drawStarLayerNear(sceneCtx, layout.nearStars, dist, speedNow, layout.minX, layout.totalFieldW, laneY, laneBottom, quality);
  if (quality !== 'low') drawComet(sceneCtx, t, cometOffset, width, height);
}

let _cachedEdgeLaneY = -1;
let _cachedTopEdgeGrad = null;
let _cachedEdgeLaneBottom = -1;
let _cachedBottomEdgeGrad = null;

function getTopEdgeRefract(ctx, laneY) {
  if (_cachedEdgeLaneY !== laneY || !_cachedTopEdgeGrad) {
    _cachedEdgeLaneY = laneY;
    _cachedTopEdgeGrad = ctx.createLinearGradient(0, laneY, 0, laneY + 8);
    _cachedTopEdgeGrad.addColorStop(0, 'rgba(4, 10, 20, 0.45)');
    _cachedTopEdgeGrad.addColorStop(1, 'rgba(4, 10, 20, 0)');
  }
  return _cachedTopEdgeGrad;
}

function getBottomEdgeRefract(ctx, laneBottom) {
  if (_cachedEdgeLaneBottom !== laneBottom || !_cachedBottomEdgeGrad) {
    _cachedEdgeLaneBottom = laneBottom;
    _cachedBottomEdgeGrad = ctx.createLinearGradient(0, laneBottom - 8, 0, laneBottom);
    _cachedBottomEdgeGrad.addColorStop(0, 'rgba(4, 10, 20, 0)');
    _cachedBottomEdgeGrad.addColorStop(1, 'rgba(4, 10, 20, 0.45)');
  }
  return _cachedBottomEdgeGrad;
}

// Composites the "under glass" scene canvas onto ctx: sky/void bands blit
// 1:1 across the full padded vertical span [-VOIDGLASS_PAD_Y, height + VOIDGLASS_PAD_Y],
// while the lane band blits row-by-row with a sinusoidal x-offset (the
// sandbox's applyGlassRefraction), sourcing safely from the distinct scene
// canvas rather than a self-read of the live destination.
function applyVoidglassRefraction(ctx, sceneCanvas, width, height, laneY, laneH, t, quality) {
  const laneBottom = laneY + laneH;
  const padY = VOIDGLASS_PAD_Y;
  const topBandH = laneY + padY;
  const botBandH = (height + padY) - laneBottom;
  const laneSrcY = laneY + padY;

  // Sky bands above/below the lane: straight 1:1 blit across the vertically padded range.
  if (topBandH > 0) {
    ctx.drawImage(sceneCanvas, 0, 0, width, topBandH, 0, -padY, width, topBandH);
  }
  if (botBandH > 0) {
    ctx.drawImage(sceneCanvas, 0, laneBottom + padY, width, botBandH, 0, laneBottom, width, botBandH);
  }

  if (quality === 'low') {
    // No refraction pass at low quality - lane band still needs the scene
    // content underneath it, just undistorted (matches the sandbox, where
    // applyGlassRefraction bails immediately and the direct draws stand).
    ctx.drawImage(sceneCanvas, 0, laneSrcY, width, laneH, 0, laneY, width, laneH);
    return;
  }

  ctx.save();
  ctx.beginPath();
  ctx.rect(0, laneY, width, laneH);
  ctx.clip();

  const step = quality === 'medium' ? 16 : 8;
  for (let y = 0; y < laneH; y += step) {
    const h = Math.min(step, laneH - y);
    const offsetX = Math.sin(t * 0.45 + (laneY + y) * 0.038) * 2.2 + Math.sin(t * 1.2 + (laneY + y) * 0.08) * 1.1;
    ctx.drawImage(sceneCanvas, 0, laneSrcY + y, width, h, Math.round(offsetX), laneY + y, width, h);
  }

  if (quality === 'high' || quality === 'ultra') {
    ctx.globalAlpha = 0.06;
    ctx.globalCompositeOperation = 'screen';
    ctx.drawImage(sceneCanvas, 0, laneSrcY, width, laneH, 2, laneY, width, laneH);
    ctx.drawImage(sceneCanvas, 0, laneSrcY, width, laneH, -2, laneY, width, laneH);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
  }

  const topEdgeRefract = getTopEdgeRefract(ctx, laneY);
  ctx.globalCompositeOperation = 'multiply';
  ctx.globalAlpha = 1.0;
  ctx.fillStyle = topEdgeRefract;
  ctx.fillRect(0, laneY, width, 8);

  const bottomEdgeRefract = getBottomEdgeRefract(ctx, laneBottom);
  ctx.fillStyle = bottomEdgeRefract;
  ctx.fillRect(0, laneBottom - 8, width, 8);

  ctx.restore();
}

// --- The glass floor itself -------------------------------------------

const PANEL_COL_W = 260;
const PANEL_ROWS = 3;
const FAN = 0.34;

function fanBottomX(topX, width) {
  return topX + (topX - width / 2) * FAN;
}

// Static physical structure: tint, milky haze, frost, mullions/rivets,
// horizontal seams, and the two slab-edge base bands (no t/dist dependency -
// safe to bake once). Drawn across the full [xStart, xEnd) range so the
// panel grid keeps extending under camera pan/zoom, mirroring how sul's own
// floor rubble extends across its padded bounds.
function drawGlassFloorStatic(ctx, xStart, xEnd, width, laneY, laneBottom, frostCanvas) {
  const spanW = xEnd - xStart;
  const tint = ctx.createLinearGradient(0, laneY, 0, laneBottom);
  tint.addColorStop(0, 'rgba(120, 170, 220, 0.07)');
  tint.addColorStop(0.5, 'rgba(90, 140, 200, 0.11)');
  tint.addColorStop(1, 'rgba(70, 110, 170, 0.17)');
  ctx.fillStyle = tint;
  ctx.fillRect(xStart, laneY, spanW, laneBottom - laneY);

  const milk = ctx.createLinearGradient(0, laneY, 0, laneBottom);
  milk.addColorStop(0, 'rgba(180, 220, 245, 0.048)');
  milk.addColorStop(0.4, 'rgba(150, 195, 230, 0.035)');
  milk.addColorStop(0.75, 'rgba(130, 180, 220, 0.048)');
  milk.addColorStop(1, 'rgba(160, 210, 245, 0.065)');
  ctx.fillStyle = milk;
  ctx.fillRect(xStart, laneY, spanW, laneBottom - laneY);

  if (frostCanvas) {
    ctx.drawImage(frostCanvas, 0, laneY);
  }

  const startCol = Math.floor(xStart / PANEL_COL_W) * PANEL_COL_W - PANEL_COL_W;
  const endCol = xEnd + PANEL_COL_W;
  for (let x = startCol; x <= endCol; x += PANEL_COL_W) {
    const bottomX = fanBottomX(x, width);
    ctx.lineWidth = 2.4;
    ctx.strokeStyle = 'rgba(2, 6, 14, 0.65)';
    ctx.beginPath();
    ctx.moveTo(x - 0.4, laneY + 3.5);
    ctx.lineTo(bottomX - 0.4, laneBottom + 3.5);
    ctx.stroke();

    ctx.lineWidth = 1.4;
    ctx.strokeStyle = 'rgba(4, 10, 20, 0.5)';
    ctx.beginPath();
    ctx.moveTo(x - 0.4, laneY);
    ctx.lineTo(bottomX - 0.4, laneBottom);
    ctx.stroke();

    ctx.lineWidth = 0.9;
    ctx.strokeStyle = 'rgba(214, 235, 255, 0.32)';
    ctx.beginPath();
    ctx.moveTo(x + 0.4, laneY);
    ctx.lineTo(bottomX + 0.4, laneBottom);
    ctx.stroke();
  }

  const laneH = laneBottom - laneY;
  const rowH = laneH / PANEL_ROWS;
  for (let row = 0; row <= PANEL_ROWS; row++) {
    const py = laneY + row * rowH;
    const frac = row / PANEL_ROWS;
    ctx.fillStyle = 'rgba(2, 6, 14, 0.55)';
    ctx.fillRect(xStart, py + 3, spanW, 1.8);
    ctx.fillStyle = 'rgba(214, 235, 255, 0.25)';
    ctx.fillRect(xStart, py, spanW, 1);
    ctx.fillStyle = 'rgba(4, 10, 20, 0.45)';
    ctx.fillRect(xStart, py + 1, spanW, 1.4);
    if (row < PANEL_ROWS) {
      ctx.fillStyle = 'rgba(214, 238, 255, 0.08)';
      ctx.fillRect(xStart, py + 2, spanW, 1);
      ctx.fillStyle = 'rgba(2, 6, 14, 0.18)';
      ctx.fillRect(xStart, py + rowH - 1, spanW, 1);
    }
    for (let x = startCol; x <= endCol; x += PANEL_COL_W) {
      const rx = x + (fanBottomX(x, width) - x) * frac;
      ctx.fillStyle = '#0a0f18';
      ctx.fillRect(rx - 2, py - 2, 4, 4);
      ctx.fillStyle = '#9fc9ec';
      ctx.fillRect(rx - 1, py - 1, 2, 2);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(rx - 0.5, py - 1, 1, 1);
    }
  }

  drawGlassSlabEdgeStatic(ctx, xStart, xEnd, laneY, -1);
  drawGlassSlabEdgeStatic(ctx, xStart, xEnd, laneBottom, 1);
}

const GLASS_THICKNESS = 11;

function drawGlassSlabEdgeStatic(ctx, xStart, xEnd, y, dir) {
  const spanW = xEnd - xStart;
  const outerY = y + dir * GLASS_THICKNESS;
  const top = dir < 0 ? outerY : y;
  const bottom = dir < 0 ? y : outerY;

  const grad = ctx.createLinearGradient(0, top, 0, bottom);
  if (dir < 0) {
    grad.addColorStop(0, 'rgba(210, 238, 255, 0.42)');
    grad.addColorStop(0.3, 'rgba(120, 200, 190, 0.18)');
    grad.addColorStop(0.75, 'rgba(90, 150, 190, 0.09)');
    grad.addColorStop(1, 'rgba(90, 150, 190, 0)');
  } else {
    grad.addColorStop(0, 'rgba(90, 150, 190, 0)');
    grad.addColorStop(0.25, 'rgba(90, 150, 190, 0.09)');
    grad.addColorStop(0.7, 'rgba(120, 200, 190, 0.18)');
    grad.addColorStop(1, 'rgba(210, 238, 255, 0.42)');
  }
  ctx.fillStyle = grad;
  ctx.fillRect(xStart, top, spanW, bottom - top);

  ctx.fillStyle = 'rgba(226, 245, 255, 0.5)';
  ctx.fillRect(xStart, y - 0.5, spanW, 1);
  ctx.fillStyle = 'rgba(226, 245, 255, 0.3)';
  ctx.fillRect(xStart, outerY - 0.5, spanW, 1);
}

let _voidglassSlabHotspotGrad = null;
function getVoidglassSlabHotspotGrad(ctx) {
  if (!_voidglassSlabHotspotGrad) {
    _voidglassSlabHotspotGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, 160);
    _voidglassSlabHotspotGrad.addColorStop(0, 'rgba(240, 252, 255, 0.28)');
    _voidglassSlabHotspotGrad.addColorStop(0.35, 'rgba(110, 235, 220, 0.16)');
    _voidglassSlabHotspotGrad.addColorStop(0.7, 'rgba(195, 130, 240, 0.08)');
    _voidglassSlabHotspotGrad.addColorStop(1, 'rgba(195, 130, 240, 0)');
  }
  return _voidglassSlabHotspotGrad;
}

// Dynamic slab-edge specular hotspot (t-driven drift + iridescent fringe).
function drawGlassSlabHotspot(ctx, width, y, dir, t) {
  const outerY = y + dir * GLASS_THICKNESS;
  const top = dir < 0 ? outerY : y;
  const bottom = dir < 0 ? y : outerY;
  const driftX = width / 2 + Math.sin(t * 0.18 + (dir < 0 ? 0 : Math.PI)) * width * 0.42;
  const midY = (top + bottom) / 2;
  ctx.save();
  ctx.translate(driftX, midY);
  ctx.globalCompositeOperation = 'screen';
  ctx.fillStyle = getVoidglassSlabHotspotGrad(ctx);
  ctx.fillRect(-driftX, top - midY, width, bottom - top);
  ctx.restore();
}

// Dynamic: starlight reflected in the glass surface (lighter blend streaks).
function drawGlassFloorReflectionStreaks(ctx, reflectionField, dist, speedNow, width, laneY, laneBottom, quality) {
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  for (const s of reflectionField) {
    const x = wrapCoord(s.x0 - dist * s.depthFactor, width + 20) - 10;
    const len = Math.min(90, speedNow * s.depthFactor * 0.1);
    drawStarStreak(ctx, x, s.y, s.r, s.tint, 0.22, len, laneY, laneBottom, quality);
  }
  ctx.restore();
}

// Static blit of pre-rendered scratches (hairline wear on the pane).
function drawScratchesStatic(ctx, scratchesCanvas, laneY) {
  if (!scratchesCanvas) return;
  ctx.drawImage(scratchesCanvas, 0, laneY);
}

// Static blit of pre-rendered internal micro-bubbles/stress fractures.
function drawInternalFlawsStatic(ctx, flawsCanvas, laneY) {
  if (!flawsCanvas) return;
  ctx.drawImage(flawsCanvas, 0, laneY);
}

// --- Cached, transparent glass-floor STRUCTURE canvas (Fix 1) --------------
// drawGlassFloorStatic()/drawInternalFlawsStatic()/drawScratchesStatic() take
// no t/dist argument anywhere - every input (layout's frost/scratch/flaw
// sub-canvases, width, laneY, laneBottom) is exactly what getLayout() already
// keys its own cache invalidation on, plus renderer.height for this sprite's
// own canvas size. So instead of re-running the same ~24 strokes + ~100
// fillRects + 4 gradients every single frame (the dynamic background used to
// call all three directly, every call), bake them once into a transparent
// offscreen canvas - lazy per-renderer singleton, same idiom as
// getSceneCanvas() above, invalidated whenever the layout it was built from
// would be (renderer._voidglassLayoutKey changing covers seed/laneY/laneH/
// width; renderer.height is checked separately since the sprite's own canvas
// dimensions include it) - and blit it back with a single drawImage().
//
// This is pixel-identical, not just visually close: all three functions (and
// everything they in turn drawImage from layout.frostCanvas/scratchesCanvas/
// flawsCanvas) use plain source-over compositing only, and Porter-Duff "over"
// is associative - painting a sequence of source-over shapes onto a blank
// transparent buffer and then compositing that buffer once onto the
// destination with source-over produces exactly the same result as painting
// the same shapes directly onto the destination in the same order.
function getGlassStructureCanvas(renderer, layout, laneY, laneBottom) {
  if (typeof document === 'undefined') return null;
  const key = `${renderer._voidglassLayoutKey}:${renderer.height}`;
  if (renderer._voidglassStructureCanvas && renderer._voidglassStructureKey === key) {
    return renderer._voidglassStructureCanvas;
  }
  const cvs = document.createElement('canvas');
  cvs.width = renderer.width;
  cvs.height = renderer.height;
  const sctx = cvs.getContext('2d');
  drawGlassFloorStatic(sctx, 0, renderer.width, renderer.width, laneY, laneBottom, layout.frostCanvas);
  drawInternalFlawsStatic(sctx, layout.flawsCanvas, laneY);
  drawScratchesStatic(sctx, layout.scratchesCanvas, laneY);
  renderer._voidglassStructureCanvas = cvs;
  renderer._voidglassStructureKey = key;
  return cvs;
}

// Dynamic prismatic glint when the moving reflection horizon sweeps over a flaw (top tier only).
function drawInternalFlawsDynamic(ctx, internalFlaws, t, width) {
  const swayX = Math.sin(t * 0.28) * 160 + Math.sin(t * 0.13 + 1.2) * 90;
  const horizonX = width / 2 + swayX;
  for (const f of internalFlaws) {
    const distToHorizon = Math.abs(f.x - horizonX);
    if (distToHorizon >= 160) continue;
    const glintFactor = 1 - distToHorizon / 160;
    const glintAlpha = Math.pow(glintFactor, 3) * 0.75;
    ctx.save();
    ctx.globalCompositeOperation = 'screen';
    ctx.fillStyle = `rgba(255, 140, 100, ${glintAlpha * 0.55})`;
    ctx.fillRect(f.x - 1.2, f.y - 0.4, 0.9, 0.9);
    ctx.fillStyle = `rgba(230, 250, 255, ${glintAlpha * 0.95})`;
    ctx.fillRect(f.x - 0.4, f.y - 0.4, 0.9, 0.9);
    ctx.fillStyle = `rgba(160, 140, 255, ${glintAlpha * 0.55})`;
    ctx.fillRect(f.x + 0.4, f.y - 0.4, 0.9, 0.9);
    ctx.restore();
  }
}

// Dynamic: soft specular reflections of planets/comet cast onto the glass deck.
function drawCelestialReflections(ctx, planets, cometOffset, t, dist, width, laneY, laneBottom, minX, totalFieldW) {
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, laneY, width, laneBottom - laneY);
  ctx.clip();
  ctx.globalCompositeOperation = 'screen';

  for (const p of planets) {
    const x = wrapCoord(p.x0 - minX - dist * p.depthFactor, totalFieldW) + minX;
    if (x < -p.r * 2 || x > width + p.r * 2) continue;
    let reflY;
    if (p.y < laneY) {
      reflY = laneY + 28 + Math.min(130, (laneY - p.y) * 0.55);
    } else if (p.y > laneBottom) {
      reflY = laneBottom - 28 - Math.min(130, (p.y - laneBottom) * 0.55);
    } else {
      reflY = p.y;
    }
    let rgb;
    switch (p.hue) {
      case 'rust': rgb = '225, 140, 100'; break;
      case 'sand': rgb = '230, 200, 145'; break;
      case 'ice': rgb = '185, 230, 255'; break;
      default: rgb = '190, 200, 220'; break;
    }
    const rx = p.r * 2.2;
    const ry = p.r * 0.65;
    if (!p._reflGrad) {
      p._reflGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, rx);
      p._reflGrad.addColorStop(0, `rgba(${rgb}, 0.08)`);
      p._reflGrad.addColorStop(0.4, `rgba(${rgb}, 0.035)`);
      p._reflGrad.addColorStop(1, `rgba(${rgb}, 0)`);
    }
    ctx.save();
    ctx.translate(x, reflY);
    ctx.fillStyle = p._reflGrad;
    ctx.beginPath();
    ctx.ellipse(0, 0, rx, ry, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  const cyclePos = (t + cometOffset) % COMET_CYCLE;
  if (cyclePos <= COMET_ACTIVE) {
    const cycleIndex = Math.floor((t + cometOffset) / COMET_CYCLE);
    const rng = mulberry32(cycleIndex * 104729 + 7);
    const startX = -60 + rng() * 280;
    const startY = 25 + rng() * (270);
    const angle = 0.14 + rng() * 0.22;
    const travelPx = 860 + rng() * 260;
    const progress = cyclePos / COMET_ACTIVE;
    const fade = progress < 0.1 ? progress / 0.1 : progress > 0.82 ? (1 - progress) / 0.18 : 1;
    const headX = startX + Math.cos(angle) * travelPx * progress;
    const headY = startY + Math.sin(angle) * travelPx * progress;
    if (headX > -50 && headX < width + 50) {
      const laneH = laneBottom - laneY;
      const reflY = laneY + Math.min(laneH - 25, Math.max(25, headY * 0.65 + 40));
      if (!_voidglassCometReflGrad) {
        _voidglassCometReflGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, 110);
        _voidglassCometReflGrad.addColorStop(0, 'rgba(180, 230, 255, 0.11)');
        _voidglassCometReflGrad.addColorStop(0.4, 'rgba(140, 210, 245, 0.04)');
        _voidglassCometReflGrad.addColorStop(1, 'rgba(140, 210, 245, 0)');
      }
      ctx.save();
      ctx.translate(headX, reflY);
      ctx.globalAlpha = fade;
      ctx.fillStyle = _voidglassCometReflGrad;
      ctx.beginPath();
      ctx.ellipse(0, 0, 90, 26, angle * 0.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }

  ctx.restore();
}

let _voidglassCometReflGrad = null;
let _glassReflGrad = null;
let _cachedReflLaneH = -1;
function getGlassReflGrad(ctx, laneH) {
  if (_cachedReflLaneH !== laneH || !_glassReflGrad) {
    _cachedReflLaneH = laneH;
    _glassReflGrad = ctx.createLinearGradient(-380, 0, 380, laneH);
    _glassReflGrad.addColorStop(0, 'rgba(140, 200, 245, 0.015)');
    _glassReflGrad.addColorStop(0.25, 'rgba(100, 225, 215, 0.045)');
    _glassReflGrad.addColorStop(0.5, 'rgba(220, 245, 255, 0.10)');
    _glassReflGrad.addColorStop(0.75, 'rgba(195, 140, 245, 0.045)');
    _glassReflGrad.addColorStop(1, 'rgba(140, 200, 245, 0.015)');
  }
  return _glassReflGrad;
}

let _glassCounterGrad = null;
function getGlassCounterGrad(ctx) {
  if (!_glassCounterGrad) {
    _glassCounterGrad = ctx.createRadialGradient(0, 0, 20, 0, 0, 340);
    _glassCounterGrad.addColorStop(0, 'rgba(200, 235, 255, 0.055)');
    _glassCounterGrad.addColorStop(0.5, 'rgba(160, 215, 245, 0.025)');
    _glassCounterGrad.addColorStop(1, 'rgba(160, 215, 245, 0)');
  }
  return _glassCounterGrad;
}

let _glassPanelGrad = null;
let _cachedPanelLaneH = -1;
function getGlassPanelGrad(ctx, laneH) {
  if (_cachedPanelLaneH !== laneH || !_glassPanelGrad) {
    _cachedPanelLaneH = laneH;
    _glassPanelGrad = ctx.createLinearGradient(0, 0, 0, laneH);
    _glassPanelGrad.addColorStop(0, 'rgba(210, 235, 255, 0.7)');
    _glassPanelGrad.addColorStop(0.5, 'rgba(180, 225, 255, 1.0)');
    _glassPanelGrad.addColorStop(1, 'rgba(140, 200, 245, 0.005)');
  }
  return _glassPanelGrad;
}

let _topFresnelGrad = null;
let _cachedFresnelLaneY = -1;
function getTopFresnelGrad(ctx, laneY) {
  if (_cachedFresnelLaneY !== laneY || !_topFresnelGrad) {
    _cachedFresnelLaneY = laneY;
    _topFresnelGrad = ctx.createLinearGradient(0, laneY, 0, laneY + 34);
    _topFresnelGrad.addColorStop(0, 'rgba(235, 248, 255, 0.09)');
    _topFresnelGrad.addColorStop(0.2, 'rgba(110, 235, 220, 0.05)');
    _topFresnelGrad.addColorStop(0.55, 'rgba(195, 135, 245, 0.04)');
    _topFresnelGrad.addColorStop(1, 'rgba(180, 140, 240, 0)');
  }
  return _topFresnelGrad;
}

let _botFresnelGrad = null;
let _cachedFresnelLaneBottom = -1;
function getBotFresnelGrad(ctx, laneBottom) {
  if (_cachedFresnelLaneBottom !== laneBottom || !_botFresnelGrad) {
    _cachedFresnelLaneBottom = laneBottom;
    _botFresnelGrad = ctx.createLinearGradient(0, laneBottom - 34, 0, laneBottom);
    _botFresnelGrad.addColorStop(0, 'rgba(180, 140, 240, 0)');
    _botFresnelGrad.addColorStop(0.45, 'rgba(195, 135, 245, 0.04)');
    _botFresnelGrad.addColorStop(0.8, 'rgba(110, 235, 220, 0.05)');
    _botFresnelGrad.addColorStop(1, 'rgba(235, 248, 255, 0.11)');
  }
  return _botFresnelGrad;
}

// Dynamic: continuously-breathing celestial glaze + Fresnel edge fringe.
function drawGlassPermanentReflection(ctx, width, laneY, laneBottom, t, quality) {
  if (quality === 'low') return;

  ctx.save();
  ctx.beginPath();
  ctx.rect(0, laneY, width, laneBottom - laneY);
  ctx.clip();

  const swayX = Math.sin(t * 0.28) * 160 + Math.sin(t * 0.13 + 1.2) * 90;
  const swayAngle = Math.sin(t * 0.21) * 35;
  const breathe = 0.5 + 0.5 * Math.sin(t * 0.4);

  ctx.globalCompositeOperation = 'screen';

  const midX = width / 2 + swayX;
  ctx.save();
  ctx.translate(midX, laneY);
  ctx.globalAlpha = 0.85 + 0.3 * breathe;
  ctx.fillStyle = getGlassReflGrad(ctx, laneBottom - laneY);
  ctx.fillRect(-midX, 0, width, laneBottom - laneY);
  ctx.restore();

  const counterX = width / 2 - Math.sin(t * 0.19 + 0.8) * 220;
  const counterY = laneY + (laneBottom - laneY) * 0.45;
  ctx.save();
  ctx.translate(counterX, counterY);
  ctx.globalAlpha = 0.8 + 0.35 * (1 - breathe);
  ctx.fillStyle = getGlassCounterGrad(ctx);
  ctx.fillRect(-counterX, laneY - counterY, width, laneBottom - laneY);
  ctx.restore();

  if (quality === 'high' || quality === 'ultra') {
    const startCol = Math.floor(-PANEL_COL_W / PANEL_COL_W) * PANEL_COL_W - PANEL_COL_W;
    const endCol = width + PANEL_COL_W;
    let colIdx = 0;
    const pGrad = getGlassPanelGrad(ctx, laneBottom - laneY);
    for (let x = startCol; x < endCol; x += PANEL_COL_W) {
      colIdx++;
      const topX1 = x;
      const topX2 = x + PANEL_COL_W;
      const botX1 = fanBottomX(topX1, width);
      const botX2 = fanBottomX(topX2, width);
      const pPhase = colIdx * 0.65;
      const pLuster = Math.sin(t * 0.35 + pPhase) * 0.5 + 0.5;
      const panelAlpha = 0.012 + pLuster * 0.028;

      ctx.save();
      ctx.globalAlpha = panelAlpha;
      ctx.fillStyle = pGrad;
      ctx.beginPath();
      ctx.moveTo(topX1 + 1, laneY + 1);
      ctx.lineTo(topX2 - 1, laneY + 1);
      ctx.lineTo(botX2 - 1, laneBottom - 1);
      ctx.lineTo(botX1 + 1, laneBottom - 1);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }
  }

  ctx.save();
  ctx.globalAlpha = 1.0 + 0.25 * Math.sin(t * 0.25);
  ctx.fillStyle = getTopFresnelGrad(ctx, laneY);
  ctx.fillRect(0, laneY, width, 34);
  ctx.restore();

  ctx.save();
  ctx.globalAlpha = 1.0 + 0.22 * Math.cos(t * 0.26);
  ctx.fillStyle = getBotFresnelGrad(ctx, laneBottom);
  ctx.fillRect(0, laneBottom - 34, width, 34);
  ctx.restore();

  ctx.restore();
}

// Extremely rare foreground pass: a massive comet flying close in front of
// the glass, occluding the lane and whatever units stand on it for an
// instant. Dynamic-foreground-only (drawn after units/particles/territory
// rail per renderForegroundCached's pipeline position).
const FG_COMET_CYCLE = 180;
const FG_COMET_ACTIVE = 0.42;


let _fgCometFloorGlow = null;
let _fgCometWakeGrad = null;
let _fgCometBodyGrad = null;
let _fgCometNucleusGrad = null;

function getFgCometFloorGlow(ctx) {
  if (!_fgCometFloorGlow) {
    _fgCometFloorGlow = ctx.createRadialGradient(0, 0, 0.1, 0, 0, 1);
    _fgCometFloorGlow.addColorStop(0, 'rgba(180, 230, 255, 0.28)');
    _fgCometFloorGlow.addColorStop(0.5, 'rgba(120, 190, 245, 0.10)');
    _fgCometFloorGlow.addColorStop(1, 'rgba(120, 190, 245, 0)');
  }
  return _fgCometFloorGlow;
}

function getFgCometWakeGrad(ctx) {
  if (!_fgCometWakeGrad) {
    _fgCometWakeGrad = ctx.createLinearGradient(-1, 0, 0, 0);
    _fgCometWakeGrad.addColorStop(0, 'rgba(120, 185, 235, 0)');
    _fgCometWakeGrad.addColorStop(0.4, 'rgba(160, 215, 250, 0.45)');
    _fgCometWakeGrad.addColorStop(0.8, 'rgba(215, 240, 255, 0.78)');
    _fgCometWakeGrad.addColorStop(1, 'rgba(245, 252, 255, 0.92)');
  }
  return _fgCometWakeGrad;
}

function getFgCometBodyGrad(ctx) {
  if (!_fgCometBodyGrad) {
    _fgCometBodyGrad = ctx.createRadialGradient(-0.25, 0, 0.1, 0, 0, 1);
    _fgCometBodyGrad.addColorStop(0, 'rgba(255, 255, 255, 0.99)');
    _fgCometBodyGrad.addColorStop(0.35, 'rgba(230, 245, 255, 0.92)');
    _fgCometBodyGrad.addColorStop(0.68, 'rgba(165, 215, 245, 0.72)');
    _fgCometBodyGrad.addColorStop(0.92, 'rgba(100, 180, 235, 0.35)');
    _fgCometBodyGrad.addColorStop(1, 'rgba(80, 160, 225, 0)');
  }
  return _fgCometBodyGrad;
}

function getFgCometNucleusGrad(ctx) {
  if (!_fgCometNucleusGrad) {
    _fgCometNucleusGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, 1);
    _fgCometNucleusGrad.addColorStop(0, 'rgba(255, 255, 255, 1.0)');
    _fgCometNucleusGrad.addColorStop(0.6, 'rgba(245, 252, 255, 0.95)');
    _fgCometNucleusGrad.addColorStop(1, 'rgba(200, 235, 255, 0.2)');
  }
  return _fgCometNucleusGrad;
}

function drawForegroundComet(ctx, t, offset, width, laneY, laneBottom, quality) {
  if (quality === 'low') return;
  const cyclePos = (t + offset) % FG_COMET_CYCLE;
  if (cyclePos > FG_COMET_ACTIVE) return;
  const progress = cyclePos / FG_COMET_ACTIVE;
  const cycleIndex = Math.floor((t + offset) / FG_COMET_CYCLE);
  const rng = mulberry32(cycleIndex * 19349663 + 29);
  const dir = rng() > 0.5 ? 1 : -1;
  const bodyR = 75 + rng() * 30;
  const fade = progress < 0.07 ? progress / 0.07 : progress > 0.88 ? (1 - progress) / 0.12 : 1;

  const startX = dir === 1 ? -320 : width + 320;
  const endX = dir === 1 ? width + 320 : -320;
  const startY = laneY + 15 + rng() * 50;
  const endY = laneBottom - 20 - rng() * 60;

  const x = startX + (endX - startX) * progress;
  const y = startY + (endY - startY) * progress;
  const angle = Math.atan2(endY - startY, endX - startX);

  ctx.save();

  ctx.save();
  ctx.beginPath();
  ctx.rect(0, laneY, width, laneBottom - laneY);
  ctx.clip();
  ctx.translate(x, y);
  ctx.scale(bodyR * 2.8, bodyR * 2.8);
  ctx.globalAlpha = fade;
  ctx.globalCompositeOperation = 'screen';
  ctx.fillStyle = getFgCometFloorGlow(ctx);
  ctx.fillRect(-2, -2, 4, 4);
  ctx.restore();

  const tailLen = 580 + rng() * 180;
  const tailWidth = bodyR * 1.5;
  const tailX = x - Math.cos(angle) * tailLen;
  const tailY = y - Math.sin(angle) * tailLen;
  const perpX = Math.cos(angle + Math.PI / 2);
  const perpY = Math.sin(angle + Math.PI / 2);

  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.scale(tailLen, 1);
  ctx.globalAlpha = fade;
  ctx.fillStyle = getFgCometWakeGrad(ctx);
  ctx.beginPath();
  ctx.moveTo(0, bodyR * 0.95);
  ctx.lineTo(-1, tailWidth * 0.3);
  ctx.lineTo(-1, -tailWidth * 0.3);
  ctx.lineTo(0, -bodyR * 0.95);
  ctx.closePath();
  ctx.fill();
  ctx.restore();

  ctx.strokeStyle = `rgba(240, 250, 255, ${0.65 * fade})`;
  ctx.lineWidth = 3.5;
  ctx.lineCap = 'round';
  for (let b = -2; b <= 2; b++) {
    const off = b * (bodyR * 0.26);
    ctx.beginPath();
    ctx.moveTo(x + perpX * off, y + perpY * off);
    ctx.lineTo(x + perpX * off - Math.cos(angle) * (tailLen * 0.55), y + perpY * off - Math.sin(angle) * (tailLen * 0.55));
    ctx.stroke();
  }

  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.scale(bodyR, bodyR);
  ctx.globalAlpha = fade;
  ctx.fillStyle = getFgCometBodyGrad(ctx);
  ctx.beginPath();
  ctx.arc(0, 0, 1, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  ctx.save();
  ctx.translate(x, y);
  ctx.scale(bodyR * 0.35, bodyR * 0.35);
  ctx.globalAlpha = fade;
  ctx.fillStyle = getFgCometNucleusGrad(ctx);
  ctx.beginPath();
  ctx.arc(0, 0, 1, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  ctx.restore();
}

// --- Voidglass cache release -------------------------------------------
export function releaseVoidglassCaches(renderer) {
  if (renderer?._voidglassSceneCanvas) {
    renderer._voidglassSceneCanvas.width = 0;
    renderer._voidglassSceneCanvas.height = 0;
    renderer._voidglassSceneCanvas = null;
    renderer._voidglassSceneCtx = null;
  }
}

// --- Voidglass mixin (mixed onto Renderer.prototype in renderer.js) -------

export const voidglassBackgroundMixin = {
  renderVoidglassStaticBackground(ctx, battlefield, customBounds = null) {
    const laneY = battlefield.laneY;
    const laneH = battlefield.laneHeight;
    const laneBottom = laneY + laneH;
    const { visibleTop, visibleBottom, visibleLeft, visibleRight } = resolveBounds(this, customBounds);
    const bgWidth = visibleRight - visibleLeft;
    const layout = getLayout(this, battlefield);

    // 1. Void base fill across the FULL padded/camera bounds - no gaps ever,
    // regardless of how far the camera pans/zooms beyond the fixed scene canvas.
    ctx.fillStyle = '#020103';
    ctx.fillRect(visibleLeft, visibleTop, bgWidth, visibleBottom - visibleTop);
    const baseGrad = ctx.createLinearGradient(0, visibleTop, 0, visibleBottom);
    baseGrad.addColorStop(0, '#02030a');
    baseGrad.addColorStop(0.5, '#03040c');
    baseGrad.addColorStop(1, '#010206');
    ctx.fillStyle = baseGrad;
    ctx.fillRect(visibleLeft, visibleTop, bgWidth, visibleBottom - visibleTop);

    // 2. Frozen (t=0, dist=0) "under glass" scene, composited across the vertically padded world window.
    const sceneCanvas = getSceneCanvas(this);
    if (sceneCanvas) {
      const sceneCtx = this._voidglassSceneCtx;
      sceneCtx.clearRect(0, 0, this.width, sceneCanvas.height);
      sceneCtx.save();
      sceneCtx.translate(0, VOIDGLASS_PAD_Y);
      drawVoidglassScene(sceneCtx, layout, this.width, this.height, 0, 0, INSTANT_SPEED, laneY, laneBottom, this.quality, 0, 0);
      sceneCtx.restore();
      applyVoidglassRefraction(ctx, sceneCanvas, this.width, this.height, laneY, laneH, 0, this.quality);
    }

    // 3. Physical glass deck structure (panels/rivets/frost/scratches/flaws),
    // extended across the full padded bounds.
    drawGlassFloorStatic(ctx, visibleLeft, visibleRight, this.width, laneY, laneBottom, layout.frostCanvas);
    drawInternalFlawsStatic(ctx, layout.flawsCanvas, laneY);
    drawScratchesStatic(ctx, layout.scratchesCanvas, laneY);

    // 4. Letterbox veil: fade smoothly at the extreme bottom of the padded vertical range
    // so the starfield remains visible below the lane in Portrait's 50% wide-zoom state
    // without a hard-clipped rectangle edge.
    const paddedBottomLimit = this.height + VOIDGLASS_PAD_Y;
    if (visibleBottom > laneBottom) {
      const veilTop = Math.max(laneBottom + 160, this.height + 60);
      const veilBottom = Math.max(veilTop + 1, paddedBottomLimit);
      if (visibleBottom > veilTop) {
        const veil = ctx.createLinearGradient(0, veilTop, 0, veilBottom);
        veil.addColorStop(0, 'rgba(7, 11, 18, 0)');
        veil.addColorStop(1, 'rgba(7, 11, 18, 1)');
        ctx.fillStyle = veil;
        ctx.fillRect(visibleLeft, veilTop, bgWidth, veilBottom - veilTop);
      }
      if (visibleBottom > veilBottom) {
        ctx.fillStyle = 'rgb(7, 11, 18)';
        ctx.fillRect(visibleLeft, veilBottom, bgWidth, visibleBottom - veilBottom);
      }
    }
  },

  renderVoidglassStaticForeground(_ctx, _battlefield, _customBounds = null) {
    // No shelf props for voidglass - open vignetted deep space above/below
    // the lane, nothing to bake here (see header note / task scope).
  },

  renderVoidglassDynamicBackground(ctx, battlefield, nowSec = null, customBounds = null) {
    const t = nowSec !== null ? nowSec : performance.now() * 0.001;
    const laneY = battlefield.laneY;
    const laneH = battlefield.laneHeight;
    const laneBottom = laneY + laneH;
    const layout = getLayout(this, battlefield);
    const dist = travelled(t);
    const speedNow = INSTANT_SPEED;
    const quality = this.quality;

    // Renderer-cached rare-event cycle offsets (plain Math.random(), never
    // seed-derived - same convention as backgroundSul.js's own
    // _sulLightningOffset/_sulMeteorOffset: cosmetic timing needs no
    // cross-peer determinism, only gameplay does).
    if (this._voidglassCometOffset === undefined) this._voidglassCometOffset = Math.random() * COMET_CYCLE;
    if (this._voidglassSatelliteOffset === undefined) this._voidglassSatelliteOffset = 360 - 90 + Math.random() * 40;

    const sceneCanvas = getSceneCanvas(this);
    if (sceneCanvas) {
      const sceneCtx = this._voidglassSceneCtx;
      sceneCtx.clearRect(0, 0, this.width, sceneCanvas.height);
      sceneCtx.save();
      sceneCtx.translate(0, VOIDGLASS_PAD_Y);
      drawVoidglassScene(
        sceneCtx, layout, this.width, this.height, t, dist, speedNow, laneY, laneBottom, quality,
        this._voidglassCometOffset, this._voidglassSatelliteOffset
      );
      sceneCtx.restore();
      applyVoidglassRefraction(ctx, sceneCanvas, this.width, this.height, laneY, laneH, t, quality);
    }

    // The refraction composite above is an opaque full-frame blit (the scene
    // canvas has no transparency), which would otherwise erase the physical
    // glass deck structure baked into the static layer underneath. This used
    // to be compensated by redrawing the entire panel grid/rivets/frost/
    // scratches raw, from scratch, every frame - the exact same heavy
    // procedural draw the static bake already does once. Instead, blit the
    // cached transparent structure sprite (see getGlassStructureCanvas()
    // above) back on top with a single drawImage() - pixel-identical,
    // far cheaper.
    const structureCanvas = getGlassStructureCanvas(this, layout, laneY, laneBottom);
    if (structureCanvas) {
      ctx.drawImage(structureCanvas, 0, 0);
    } else {
      // No document (headless/test environment without canvas support) -
      // fall back to the direct draw so behavior is unchanged there.
      drawGlassFloorStatic(ctx, 0, this.width, this.width, laneY, laneBottom, layout.frostCanvas);
      drawInternalFlawsStatic(ctx, layout.flawsCanvas, laneY);
      drawScratchesStatic(ctx, layout.scratchesCanvas, laneY);
    }

    drawGlassFloorReflectionStreaks(ctx, layout.reflectionField, dist, speedNow, this.width, laneY, laneBottom, quality);
    if (quality === 'high' || quality === 'ultra') {
      drawInternalFlawsDynamic(ctx, layout.internalFlaws, t, this.width);
    }
    drawCelestialReflections(ctx, layout.planets, this._voidglassCometOffset, t, dist, this.width, laneY, laneBottom, layout.minX, layout.totalFieldW);
    drawGlassPermanentReflection(ctx, this.width, laneY, laneBottom, t, quality);
    drawGlassSlabHotspot(ctx, this.width, laneY, -1, t);
    drawGlassSlabHotspot(ctx, this.width, laneBottom, 1, t);
  },

  renderVoidglassDynamicForeground(ctx, battlefield, nowSec = null) {
    const t = nowSec !== null ? nowSec : performance.now() * 0.001;
    const laneY = battlefield.laneY;
    const laneBottom = laneY + battlefield.laneHeight;
    this._voidglassFgCometOffset = this._voidglassFgCometOffset ?? (180 - 65 + Math.random() * 30);
    drawForegroundComet(ctx, t, this._voidglassFgCometOffset, this.width, laneY, laneBottom, this.quality);

    const layout = getLayout(this, battlefield);
    const dist = travelled(t);
    drawForegroundStars(ctx, layout.fgStars, t, dist, INSTANT_SPEED, layout.minX, layout.totalFieldW, this.width, this.height, this.quality);
    drawForegroundStars(ctx, layout.fgStars2, t, dist, INSTANT_SPEED, layout.minX, layout.totalFieldW, this.width, this.height, this.quality);
  },

  // No shelf/critters for voidglass (open deep space above/below the lane).
  renderVoidglassForegroundCritters(_ctx, _battlefield, _nowSec = null) {},

  // No weather system for deep space - explicit no-ops so renderer.js's
  // mapId branches never fall through to the human map's Earth-rain weather.js.
  renderVoidglassWeatherBackground(_ctx, _battlefield) {},
  renderVoidglassWeather(_ctx, _battlefield) {},

  // Screen-space post-process overlay (top/bottom deep-space atmosphere
  // framing) - NOT part of the 7-method static/dynamic contract above,
  // called directly from postProcessEffects.js's renderPostProcess() after the camera transform
  // has already been undone (ctx.restore()'d), same category as
  // renderObservationGlassCanopy. Resets the transform itself and works in
  // device pixels (this.canvas.width/height), matching that method's own
  // pattern. Used to also draw its own vignette/chromatic-fringe glass
  // framing here, but that duplicated the shared cockpit canopy (now
  // rendered identically on every map, see renderObservationGlassCanopy) -
  // this keeps only the top/bottom deep-space atmosphere, which is genuinely
  // specific to Voidglass rather than part of the physical window itself.
  renderVoidglassFrame(ctx, battlefield) {
    const width = this.canvas.width;
    const height = this.canvas.height;

    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    // Top/bottom deep-space atmosphere framing, projected to the lane's
    // actual on-screen position via the viewport (falls back to a fixed
    // fraction of the canvas when no viewport is attached, e.g. in tests).
    let laneScreenY = height * 0.326; // 176/540 fallback fraction
    let laneBottomScreenY = height * 0.919; // 496/540 fallback fraction
    if (this.viewport && battlefield) {
      const dpr = this.viewport.dpr || 1;
      const topPt = this.viewport.worldToScreen(0, battlefield.laneY);
      const botPt = this.viewport.worldToScreen(0, battlefield.laneY + battlefield.laneHeight);
      laneScreenY = Math.max(0, Math.min(height, topPt.y * dpr));
      laneBottomScreenY = Math.max(0, Math.min(height, botPt.y * dpr));
    }

    if (laneScreenY > 0) {
      if (this._lastLaneScreenY !== laneScreenY || !this._cachedTopAtm) {
        this._lastLaneScreenY = laneScreenY;
        this._cachedTopAtm = ctx.createLinearGradient(0, 0, 0, laneScreenY);
        this._cachedTopAtm.addColorStop(0, 'rgba(1, 2, 7, 0.45)');
        this._cachedTopAtm.addColorStop(1, 'rgba(1, 2, 7, 0)');
      }
      ctx.fillStyle = this._cachedTopAtm;
      ctx.fillRect(0, 0, width, laneScreenY);
    }
    if (laneBottomScreenY < height) {
      if (this._lastLaneBotScreenY !== laneBottomScreenY || this._lastHeight !== height || !this._cachedBotAtm) {
        this._lastLaneBotScreenY = laneBottomScreenY;
        this._lastHeight = height;
        this._cachedBotAtm = ctx.createLinearGradient(0, laneBottomScreenY, 0, height);
        this._cachedBotAtm.addColorStop(0, 'rgba(1, 2, 7, 0)');
        this._cachedBotAtm.addColorStop(1, 'rgba(1, 2, 7, 0.45)');
      }
      ctx.fillStyle = this._cachedBotAtm;
      ctx.fillRect(0, laneBottomScreenY, width, height - laneBottomScreenY);
    }

    ctx.restore();
  }
};
